<template>
    <Head :title="title" />

    <PlayerShell
        ref="shell"
        :tracks="tracks"
        :same-timeline="false"
        :initial-mode="initialMode"
        :resume="resume"
        :locale="mediaLocale"
        :traffic-blocked="trafficBlocked"
        :unavailable="tracks.length === 0"
        fail-on-fatal
        @player-event="analytics.handle"
        @player-error="analytics.error"
        @ready="onReady"
    >
        <template #unavailable>
            <VideoUnavailable :title="t('Lesson unavailable')" :body="unavailableBody" />
        </template>

        <template #poster="{ play, track }">
            <PortalPoster slot="centered-chrome" :artwork="track.artworkUrl" :title="track.title" @play="play" />
        </template>
    </PlayerShell>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref } from "vue"
import { Head } from "@inertiajs/vue3"
import PlayerShell from "../PlayerCore/PlayerShell.vue"
import VideoUnavailable from "../PlayerCore/Components/VideoUnavailable.vue"
import { registerMediaChromeLocale } from "../PlayerCore/mediaChromeLang.js"
import { provideCoreI18n } from "../PlayerCore/useCoreI18n.js"
import PortalPoster from "../PortalPlayer/Components/PortalPoster.vue"
import { createPortalTranslator } from "../PortalPlayer/i18n.js"
import { createPortalAnalytics } from "../PortalPlayer/portalAnalytics.js"
import { createPortalControl } from "../PortalPlayer/portalControl.js"
import { createPlayerBridge } from "../PortalPlayer/portalMessages.js"

/**
 * SC-1323 §4.4: the learning portal's Portal Player. The same PlayerShell as the podcast embed,
 * with the lesson's EPISODE/VIDEO items as separate tracks; statistics go to the portal as
 * springcast-portal messages (no podcast.* route is ever called).
 */
const props = defineProps({
    tracks: { type: Array, required: true },
    initialMode: { type: String, default: "video" },
    bridge: { type: Object, required: true },
    locale: { type: String, default: "en" },
    trafficBlocked: { type: Boolean, default: false },
})

// media-chrome renders its labels in each element's constructor: register before the shell mounts.
const mediaLocale = registerMediaChromeLocale(props.locale)
const t = createPortalTranslator(mediaLocale)
provideCoreI18n(t)

const unavailableBody = t("This lesson can't be played right now. Please try again later.")
const title = computed(() => props.tracks[0]?.title ?? "")
const shell = ref(null)
// Resume seconds per track id: filled by the portal's init, read by the shell on every toggle.
const resume = reactive({})
const control = createPortalControl({ player: () => shell.value, tracks: props.tracks, resume })

// Created in setup, not onMounted: the shell reports a blocked/unavailable state from its own
// mounted hook, which runs before this page's.
const bridge = createPlayerBridge({ win: window, parent: window.parent, origin: props.bridge.origin, onMessage: (message) => control.handle(message) })
const analytics = createPortalAnalytics({ post: (type, fields) => bridge.post(type, fields) })

function onReady() {
    const fields = control.readyFields()

    if (fields) {
        bridge.post("ready", fields)
    }
}

onBeforeUnmount(() => bridge.destroy())
</script>
